import { Eye, EyeOff } from 'lucide-react';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@shared/components/ui/Input';
import { cn } from '@shared/components/ui/cn';

/**
 * Password field with a show/hide toggle (DEV-3110). Wraps the shared
 * {@link Input}; forwards ref + all native input props so it composes with
 * react-hook-form `register()`. The toggle button is excluded from the tab
 * order to keep keyboard flow on the inputs.
 */
export const PasswordInput = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ className, ...props }, ref) => {
    const { t } = useTranslation('auth');
    const [visible, setVisible] = useState(false);

    return (
      <div className="relative">
        <Input
          ref={ref}
          type={visible ? 'text' : 'password'}
          className={cn('pe-10', className)}
          {...props}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => {
            setVisible((v) => !v);
          }}
          aria-label={
            visible ? t('hidePassword', 'Hide password') : t('showPassword', 'Show password')
          }
          aria-pressed={visible}
          className="absolute inset-y-0 end-0 flex items-center pe-3 text-muted-foreground hover:text-foreground"
        >
          {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';
